import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Dato } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Spunta, Tabella } from '../../componenti/base.tsx'
import { IconaBusta, IconaPiu } from '../../componenti/Icone.tsx'
import * as posta from '../../servizi/posta.ts'
import {
  FirmaDellaCasella, ScegliPersone, SceltaCasellaProgetto, casellaIniziale, controllaCasella, corpoCasella,
  type Candidato, type PersonaScelta, type ValoreCasella,
} from './CaselleCondivise.tsx'

/**
 * «Posta del progetto» (504), nella pagina del progetto: la casella dedicata, chi accede e con che
 * livello, i messaggi non letti, il comando che apre la casella nella pagina Posta.
 *
 * I comandi seguono quello che l'API dice di chi guarda (`puo`): crea, disattiva e rinomina chi gestisce
 * il progetto o le utenze; decide chi altro accede chi ha diritto alla casella (referente e Project
 * Manager del progetto) e chi gestisce le utenze; apre la casella chi vi accede, a webmail attiva.
 * Persone e messaggi non letti li vede solo chi accede alla casella o ne decide gli accessi.
 */

const testo = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const TITOLI: Record<NonNullable<posta.PersonaCasellaProgetto['diritto']>, string> = {
  referente: 'referente CIDIS', project_manager: 'Project Manager del progetto',
}

export function PostaDelProgetto({ progettoId, gestisceUtenze }: { progettoId: string; gestisceUtenze: boolean }) {
  const [dati, setDati] = useState<posta.PostaDelProgetto | null>(null)
  const [nascosta, setNascosta] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [finestra, setFinestra] = useState<'crea' | 'modifica' | null>(null)
  const [candidati, setCandidati] = useState<Candidato[] | null>(null)
  const [nuova, setNuova] = useState<PersonaScelta[]>([])
  const [inCorso, setInCorso] = useState<string | null>(null)

  const carica = useCallback(() => {
    posta.leggiPostaDelProgetto(progettoId).then(setDati).catch((e) => {
      // Chi non vede la sezione riceve 404: la sezione non c'e'.
      if (e instanceof ErroreApi && e.stato === 404) { setNascosta(true); return }
      setErrore(testo(e))
    })
  }, [progettoId])
  useEffect(carica, [carica])

  // Le persone da proporre servono a chi crea la casella o ne decide gli accessi.
  const servonoCandidati = !!dati && (dati.puo.creare || dati.puo.decidere)
  useEffect(() => {
    if (!servonoCandidati || candidati !== null) return
    posta.candidatiCasellaProgetto(progettoId).then(setCandidati).catch(() => setCandidati([]))
  }, [servonoCandidati, candidati, progettoId])

  if (nascosta) return null
  if (!dati) {
    return (
      <Riquadro titolo="Posta del progetto">
        {errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa testo="Caricamento…" />}
      </Riquadro>
    )
  }

  const c = dati.casella

  async function livello(persona: posta.PersonaCasellaProgetto, nuovo: posta.LivelloCasella) {
    setInCorso(persona.id); setErrore(null); setMessaggio(null)
    try {
      await posta.impostaPersonaProgetto(progettoId, persona.id, nuovo)
      setMessaggio(`${persona.nome} ${persona.cognome}: ${posta.ETICHETTE_LIVELLO[nuovo]}.`)
      carica()
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  async function togli(persona: posta.PersonaCasellaProgetto) {
    setInCorso(persona.id); setErrore(null); setMessaggio(null)
    try {
      await posta.togliPersonaProgetto(progettoId, persona.id)
      setMessaggio(`${persona.nome} ${persona.cognome} non accede più alla casella`
        + `${persona.diritto ? ', salvo il suo titolo di diritto' : ''}.`)
      carica()
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  async function aggiungi() {
    setInCorso('aggiungi'); setErrore(null); setMessaggio(null)
    try {
      for (const p of nuova) await posta.impostaPersonaProgetto(progettoId, p.id, p.livello)
      setMessaggio(nuova.length === 1 ? 'Persona aggiunta.' : `${nuova.length} persone aggiunte.`)
      setNuova([])
      carica()
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  const presenti = (dati.persone ?? []).map((p) => p.id)

  return (
    <Riquadro titolo="Posta del progetto"
      azioni={
        <span className="riga">
          {dati.puo.aprire && c && (
            <Link className="bottone bottone-secondario bottone-piccolo" to={`/posta?casella=${c.id}`}>
              <IconaBusta /> Apri in Posta
            </Link>
          )}
          {dati.puo.configurare && (
            <Bottone variante="secondario" dimensione="piccolo" onClick={() => setFinestra('modifica')}>Modifica</Bottone>
          )}
          {dati.puo.creare && (
            <Bottone variante="primario" dimensione="piccolo" icona={<IconaPiu />} onClick={() => setFinestra('crea')}>
              Crea la casella
            </Bottone>
          )}
        </span>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}

      {!c ? (
        <p className="tenue">
          Il progetto non ha una casella di posta dedicata.
          {dati.puo.creare ? ' Con «Crea la casella» se ne sceglie l’indirizzo e chi la usa.' : ''}
        </p>
      ) : (
        <>
          <dl className="rete-dati">
            <Dato nome="Indirizzo">
              <span className="rete-con-etichetta">
                <span className="mono">{c.indirizzo}</span>
                {!c.attiva && <Etichetta tono="attenzione">disattivata</Etichetta>}
              </span>
            </Dato>
            <Dato nome="Nome che vede chi riceve">{c.nome_visualizzato}</Dato>
            <Dato nome="Ricezione">
              {c.modalita === 'imap' ? 'dal server di posta (IMAP)'
                : dati.ricezione_attiva || c.ultimo_arrivo_il ? posta.ultimoMessaggio(c.ultimo_arrivo_il)
                  : 'non ancora attiva'}
            </Dato>
            {dati.livello && <Dato nome="Il tuo accesso">{posta.ETICHETTE_LIVELLO[dati.livello]}</Dato>}
            {dati.non_letti !== null && <Dato nome="Da leggere in arrivo">{String(dati.non_letti)}</Dato>}
          </dl>
          {c.modalita === 'ospitata' && !dati.ricezione_attiva && !c.ultimo_arrivo_il && (
            <Avviso tipo="informazione">{posta.RICEZIONE_NON_ATTIVA}</Avviso>
          )}
          {dati.livello && !dati.webmail_attiva && (
            <p className="piccolo tenue">
              La webmail non è attiva: la casella si aprirà dalla pagina Posta quando lo sarà.
            </p>
          )}

          {dati.persone && (
            <>
              <h3 className="rete-titoletto">Chi accede</h3>
              <Tabella intestazioni={['Persona', 'Accesso', ...(dati.puo.decidere
                ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}>
                {dati.persone.map((p) => (
                  <tr key={p.id}>
                    <td data-etichetta="Persona">
                      {p.cognome} {p.nome}
                      {p.stato !== 'attivo' && <> <Etichetta tono="attenzione">{p.stato}</Etichetta></>}
                    </td>
                    <td data-etichetta="Accesso" className="piccolo">
                      {p.livello ? posta.ETICHETTE_LIVELLO[p.livello] : 'nessuno: utenza non attiva'}
                      {p.diritto && <div className="tenue">di diritto: {TITOLI[p.diritto]}</div>}
                    </td>
                    {dati.puo.decidere && (
                      <td data-etichetta="Azioni">
                        {p.livello_membro ? (
                          <span className="riga riga-a-destra">
                            <Scelta etichetta={`Livello di ${p.nome} ${p.cognome}`} valore={p.livello_membro}
                                    disabilitato={inCorso === p.id}
                                    opzioni={[{ valore: 'legge', etichetta: 'legge' }, { valore: 'scrive', etichetta: 'legge e scrive' }]}
                                    cambia={(v) => void livello(p, v as posta.LivelloCasella)} />
                            <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === p.id}
                                     aria-label={`Togli ${p.nome} ${p.cognome}`} onClick={() => void togli(p)}>
                              Togli
                            </Bottone>
                          </span>
                        ) : <span className="tenue piccolo">di diritto</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </Tabella>
              {dati.puo.decidere && (
                <div className="riquadro-sezione">
                  <ScegliPersone scelte={nuova} cambia={setNuova} candidati={candidati} escluse={presenti} />
                  {nuova.length > 0 && (
                    <Bottone variante="primario" dimensione="piccolo" inCorso={inCorso === 'aggiungi'}
                             onClick={() => void aggiungi()}>
                      {nuova.length === 1 ? 'Dai l’accesso' : `Dai l’accesso a ${nuova.length} persone`}
                    </Bottone>
                  )}
                  <p className="piccolo tenue">
                    Ogni aggiunta, cambio di livello e rimozione resta nel registro. Chi perde l’incarico di
                    Project Manager sul progetto o smette di essere referente perde l’accesso di diritto subito.
                  </p>
                </div>
              )}
            </>
          )}
          {/* La firma propria della casella (508): la decide chi decide gli accessi. */}
          {dati.puo.decidere && (
            <FirmaDellaCasella casellaId={c.id} firma={c.firma ?? null}
                               salvata={(firma) => setDati((d) => (d?.casella ? { ...d, casella: { ...d.casella, firma } } : d))} />
          )}
        </>
      )}

      {finestra === 'crea' && (
        <CreaCasella progettoId={progettoId} dati={dati} candidati={candidati} puoEsistente={gestisceUtenze}
                     chiudi={() => setFinestra(null)}
                     creata={(indirizzo) => { setFinestra(null); setMessaggio(`Casella ${indirizzo} creata.`); carica() }} />
      )}
      {finestra === 'modifica' && c && (
        <ModificaCasella progettoId={progettoId} casella={c} chiudi={() => setFinestra(null)}
                         salvata={(t) => { setFinestra(null); setMessaggio(t); carica() }} />
      )}
    </Riquadro>
  )
}

function CreaCasella({ progettoId, dati, candidati, puoEsistente, chiudi, creata }: {
  progettoId: string; dati: posta.PostaDelProgetto; candidati: Candidato[] | null; puoEsistente: boolean
  chiudi: () => void; creata: (indirizzo: string) => void
}) {
  const [valore, setValore] = useState<ValoreCasella>(() => casellaIniziale(dati.nome_proposto))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function crea() {
    const trovati = controllaCasella(valore)
    setErrori(trovati)
    if (Object.keys(trovati).length) return
    setInCorso(true); setErrore(null)
    try {
      const r = await posta.creaCasellaProgetto(progettoId, corpoCasella(valore))
      creata(r.indirizzo)
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Casella di posta del progetto" larga chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} onClick={() => void crea()}>Crea la casella</Bottone>
      </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p className="piccolo tenue">
        La casella è del progetto: nessun inoltro verso le caselle personali. Si apre dalla pagina Posta o da
        qui. {dati.ricezione_attiva ? '' : posta.RICEZIONE_NON_ATTIVA}
      </p>
      <SceltaCasellaProgetto valore={valore} cambia={setValore} candidati={candidati}
                             puoEsistente={puoEsistente} errori={errori} />
    </Modale>
  )
}

function ModificaCasella({ progettoId, casella, chiudi, salvata }: {
  progettoId: string; casella: NonNullable<posta.PostaDelProgetto['casella']>
  chiudi: () => void; salvata: (testo: string) => void
}) {
  const [nome, setNome] = useState(casella.nome_visualizzato)
  const [attiva, setAttiva] = useState(casella.attiva)
  // L'indirizzo si cambia solo finche' la casella e' vuota, e solo se ha la forma delle caselle nuove.
  const forma = /^(progetto|pg)-(.+)@/.exec(casella.indirizzo)
  const cambiabile = casella.messaggi === 0 && !!forma
  const [prefisso, setPrefisso] = useState<posta.PrefissoProgetto>((forma?.[1] as posta.PrefissoProgetto) ?? 'progetto')
  const [parte, setParte] = useState(forma?.[2] ?? '')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const erroreParte = cambiabile ? posta.parteValida(parte) : null

  async function salva() {
    if (erroreParte) return
    setInCorso(true); setErrore(null)
    try {
      const nuovoIndirizzo = cambiabile && `${prefisso}-${parte.trim()}@${posta.DOMINIO_PROGETTI}` !== casella.indirizzo
      const r = await posta.modificaCasellaProgetto(progettoId, {
        nome_visualizzato: nome.trim(), attiva,
        ...(nuovoIndirizzo ? { prefisso, parte: parte.trim() } : {}),
      })
      salvata(r.modificata ? 'Casella aggiornata.' : 'Nessuna modifica.')
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={`Casella ${casella.indirizzo}`} chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={nome.trim().length < 2 || !!erroreParte}
                 onClick={() => void salva()}>Salva</Bottone>
      </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Campo etichetta="Nome che vede chi riceve" obbligatorio maxLength={120} valore={nome} cambia={setNome} />
      {cambiabile ? (
        <div className="griglia-modulo">
          <Scelta etichetta="Prefisso" valore={prefisso} cambia={(v) => setPrefisso(v as posta.PrefissoProgetto)}
                  opzioni={posta.PREFISSI_PROGETTO.map((p) => ({ valore: p, etichetta: `${p}-` }))} />
          <Campo etichetta="Dopo il trattino" maxLength={40} valore={parte}
                 cambia={(v) => setParte(v.toLowerCase().replace(/\s+/g, ''))} errore={erroreParte}
                 aiuto="L’indirizzo si cambia finché la casella non ha messaggi." />
        </div>
      ) : (
        <p className="piccolo tenue">
          L’indirizzo non si cambia: {casella.messaggi ? 'la casella ha già dei messaggi' : 'è quello di una casella esistente'}.
        </p>
      )}
      <Spunta etichetta="Casella attiva" valore={attiva} cambia={setAttiva}
              aiuto="Disattivata, nessuno la apre e la ricezione la rifiuta; la posta già arrivata resta e torna con la riattivazione." />
    </Modale>
  )
}
